/**
 * Episode of Care Lifecycle Tracker — main page with stage tabs, drawer, matching panel.
 *
 * Data source is mode-aware via `useServiceRequestTracker`:
 *  - Mock mode: legacy `mockLifecycleData` (unchanged).
 *  - Live mode: Medplum `ServiceRequest` + linked `EpisodeOfCare` / `Patient`
 *    mapped by `lib/serviceRequestFhirMapping.ts`.
 */

import { Badge, Button, Container, Group, Loader, Modal, Stack, Tabs, Text, Title, Paper } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMedplum } from '@medplum/react-hooks';
import type { Organization } from '@medplum/fhirtypes';
import type { ClientSummary, CoverageSummary, LifecycleStage } from './types/lifecycle';
import type { EpisodeRow, PartialSubmission, VerificationErrorRow } from './types/lifecycle';
import { StageTab } from './components/StageTab';
import { LocalhostOnlyBadge } from '../../components/LocalhostOnlyBadge';
import { useEffectiveDisplayUserRole } from '../../lib/displayUserRole';
import { ClientDetailDrawer } from './components/ClientDetailDrawer';
import { MatchingModal } from './components/MatchingModal';
import { ManualAssignModal } from './components/ManualAssignModal';
import { VerificationActionDrawer } from './components/VerificationActionDrawer';
import { EpisodeActionDrawer } from './components/EpisodeActionDrawer';
import { ScreeningFormModal } from './components/ScreeningFormModal';
import { ScreeningResultsModal } from './components/ScreeningResultsModal';
import { ScheduleCallModal } from './components/ScheduleCallModal';
import { ConsultationNoteModal } from './components/ConsultationNoteModal';
import { TransferRequestModal } from './components/TransferRequestModal';
import { TransferModal, type TransferModalContext } from '../caseload/components/TransferModal';
import {
  mockClientSummary,
  mockCoverages,
} from './data/mockLifecycleData';
import { STAGE_LABELS } from './data/stageConfig';
import type { StageRow } from './components/StageTab';
import { useServiceRequestTracker } from '../../hooks/useServiceRequestTracker';
import { EligibilityTable } from '../eligibility/EligibilityTable';
import { EligibilityActionModal } from '../eligibility/EligibilityActionModal';
import { toEligibilityRow, type EligibilityRow } from '../eligibility/types';
import { useClients } from '../../contexts/ClientsContext';
import { TrackerAddEpisodeModal } from './components/TrackerAddEpisodeModal';
import { useTrackerPresence } from './hooks/useTrackerPresence';
import {
  readServiceRequestCreateEocPatientId,
  SERVICE_REQUEST_CREATE_EOC_PARAM,
  SERVICE_REQUEST_PATIENT_ID_PARAM,
} from './serviceRequestTrackerNavigation';

/** Live tracker row → drawer client summary (no mock Maria Santos / fake episodes). */
function liveClientSummaryFromRow(row: StageRow | null, episode: EpisodeRow | null): ClientSummary | null {
  if (!row) return null;
  const r = row as Partial<EpisodeRow>;
  return {
    id: String(r.clientId ?? r.id ?? ''),
    name: r.clientName ?? 'Client',
    email: r.email,
    episodes: episode ? [episode] : [],
  };
}

function liveCoveragesFromEpisodeRow(ep: EpisodeRow | null): CoverageSummary[] {
  if (!ep?.payer?.trim()) return [];
  return [
    {
      id: `tracker-cov-${ep.id}`,
      payer: ep.payer,
      serviceTypeCode: 'SR',
      status: 'pending',
      paymentType: 'insurance',
    },
  ];
}

/** Build the FHIR transfer context for a fulfilled tracker row (row id === ServiceRequest.id). */
function transferContextFromRow(row: EpisodeRow): TransferModalContext | null {
  if (!row.episodeOfCareId) return null;
  return {
    patientId: row.clientId,
    patientDisplay: row.clientName,
    episodeOfCareId: row.episodeOfCareId,
    episodeDisplay: row.episodeServiceType,
    serviceRequestId: row.id,
    currentProviderDisplay: row.assignedProvider,
    serviceTypeDisplay: row.episodeServiceType,
    episodeOptions: [
      {
        value: row.episodeOfCareId,
        label: row.episodeServiceType,
        currentProviderDisplay: row.assignedProvider,
        serviceTypeDisplay: row.episodeServiceType,
      },
    ],
  };
}

const TABS_ORDER: LifecycleStage[] = [
  'all',
  'needs-verification',
  'waitlist',
  'screening',
  'matching',
  'consultation',
  'transfer',
  'cancelled',
  'fulfilled',
];

export function EpisodeTrackerPage(): JSX.Element {
  const userRole = useEffectiveDisplayUserRole();
  const [activeTab, setActiveTab] = useState<LifecycleStage | null>('all');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [focusedRow, setFocusedRow] = useState<StageRow | null>(null);
  const [focusedEpisode, setFocusedEpisode] = useState<EpisodeRow | null>(null);
  const [matchingPanelOpen, setMatchingPanelOpen] = useState(false);
  const [matchingRow, setMatchingRow] = useState<EpisodeRow | null>(null);
  const [manualAssignRow, setManualAssignRow] = useState<EpisodeRow | null>(null);
  const [verificationAction, setVerificationAction] = useState<{ action: string; row: StageRow } | null>(null);
  const [episodeAction, setEpisodeAction] = useState<{ action: string; row: StageRow } | null>(null);
  const [eligibilityActionRow, setEligibilityActionRow] = useState<EligibilityRow | null>(null);
  const [screeningActionRow, setScreeningActionRow] = useState<StageRow | null>(null);
  const [screeningResultsRow, setScreeningResultsRow] = useState<StageRow | null>(null);
  const [scheduleCallRow, setScheduleCallRow] = useState<StageRow | null>(null);
  const [uninterestedRow, setUninterestedRow] = useState<StageRow | null>(null);
  const [consultationActionRow, setConsultationActionRow] = useState<StageRow | null>(null);
  const [transferRequestRow, setTransferRequestRow] = useState<EpisodeRow | null>(null);
  const [initiateTransferRow, setInitiateTransferRow] = useState<EpisodeRow | null>(null);
  const [addEpisodeOpen, setAddEpisodeOpen] = useState(false);
  const [addEpisodePatientId, setAddEpisodePatientId] = useState<string | undefined>();
  const [searchParams, setSearchParams] = useSearchParams();

  const tracker = useServiceRequestTracker();
  const { reloadCaseload } = useClients();
  const stageCounts = tracker.stageCounts;
  const rowsByStage = tracker.rowsByStage;

  const { presenceByRow, setActiveRow } = useTrackerPresence();

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && (TABS_ORDER as string[]).includes(tab)) {
      setActiveTab(tab as LifecycleStage);
    }
  }, [searchParams]);

  useEffect(() => {
    const patientId = readServiceRequestCreateEocPatientId(searchParams);
    if (!patientId) return;
    setAddEpisodePatientId(patientId);
    setAddEpisodeOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete(SERVICE_REQUEST_CREATE_EOC_PARAM);
    next.delete(SERVICE_REQUEST_PATIENT_ID_PARAM);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleCloseAddEpisode = useCallback(() => {
    setAddEpisodeOpen(false);
    setAddEpisodePatientId(undefined);
  }, []);

  const handleOpenAddEpisode = useCallback(() => {
    setAddEpisodePatientId(undefined);
    setAddEpisodeOpen(true);
  }, []);

  // Broadcast which row this user is acting on, derived strictly from open modal/drawer state.
  useEffect(() => {
    const openRow =
      (drawerOpen && focusedRow) ||
      (matchingPanelOpen && matchingRow) ||
      manualAssignRow ||
      verificationAction?.row ||
      episodeAction?.row ||
      eligibilityActionRow ||
      screeningActionRow ||
      screeningResultsRow ||
      scheduleCallRow ||
      uninterestedRow ||
      consultationActionRow ||
      transferRequestRow ||
      initiateTransferRow ||
      null;
    const rowId = openRow ? String((openRow as { id?: unknown }).id ?? '') : '';
    setActiveRow(rowId || null);
  }, [
    drawerOpen,
    focusedRow,
    matchingPanelOpen,
    matchingRow,
    manualAssignRow,
    verificationAction,
    episodeAction,
    eligibilityActionRow,
    screeningActionRow,
    screeningResultsRow,
    scheduleCallRow,
    uninterestedRow,
    consultationActionRow,
    transferRequestRow,
    initiateTransferRow,
    setActiveRow,
  ]);

  const eligibilityRows = useMemo(
    () => (rowsByStage['needs-verification'] ?? []).map(toEligibilityRow),
    [rowsByStage],
  );

  // Practice filter options pulled from the actual practice Organizations.
  const medplum = useMedplum();
  const [practiceOptions, setPracticeOptions] = useState<string[]>([]);
  useEffect(() => {
    let cancelled = false;
    medplum
      .searchResources('Organization', { type: 'prov', _count: '200' })
      .then((orgs: Organization[]) => {
        if (cancelled) return;
        const names = [...new Set(orgs.map((o) => o.name?.trim()).filter((n): n is string => Boolean(n)))];
        names.sort((a, b) => a.localeCompare(b));
        setPracticeOptions(names);
      })
      .catch(() => {
        if (!cancelled) setPracticeOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [medplum]);

  const handleRowClick = useCallback((row: StageRow) => {
    setFocusedRow(row);
    const ep = row as Partial<EpisodeRow>;
    if (ep.clientId != null) {
      setFocusedEpisode(ep as unknown as EpisodeRow);
      setDrawerOpen(true);
    } else if ((row as Partial<VerificationErrorRow>).clientName) {
      setFocusedEpisode(null);
      setDrawerOpen(true);
    } else if ((row as Partial<PartialSubmission>).lastStepReached != null) {
      setFocusedEpisode(null);
      setDrawerOpen(true);
    }
  }, []);

  const handleOpenMatching = useCallback((row: StageRow) => {
    setMatchingRow(row as unknown as EpisodeRow);
    setMatchingPanelOpen(true);
  }, []);

  const handleRowAction = useCallback((action: string, row: StageRow) => {
    if (action === 'View Chart' || action === 'View Partial Submission' || action === 'View Client Insurance') {
      handleRowClick(row);
    } else {
      setEpisodeAction({ action, row });
    }
  }, [handleRowClick]);

  const effectiveStageForRow = useCallback((row: StageRow): LifecycleStage => {
    if (activeTab && activeTab !== 'all') return activeTab;
    return (row.stage as LifecycleStage) ?? 'all';
  }, [activeTab]);

  const handleContextualAction = useCallback((row: StageRow) => {
    const stage = effectiveStageForRow(row);
    switch (stage) {
      case 'screening':
        setScreeningActionRow(row);
        break;
      case 'matching':
        handleOpenMatching(row);
        break;
      case 'consultation':
        setConsultationActionRow(row);
        break;
      case 'active':
      case 'transfer':
      case 'inactive':
      case 'all':
        handleRowClick(row);
        break;
      default:
        break;
    }
  }, [effectiveStageForRow, handleOpenMatching, handleRowClick]);

  const drawerServiceRequest = useMemo(() => {
    if (!focusedRow) return undefined;
    const srId = String(focusedRow.id ?? '');
    return tracker.serviceRequests.find((sr) => sr.id === srId);
  }, [focusedRow, tracker.serviceRequests]);

  const matchingServiceRequest = useMemo(() => {
    if (!matchingRow) return undefined;
    return tracker.serviceRequests.find((sr) => sr.id === String(matchingRow.id ?? ''));
  }, [matchingRow, tracker.serviceRequests]);

  const transferRequestServiceRequest = useMemo(() => {
    if (!transferRequestRow) return undefined;
    return tracker.serviceRequests.find((sr) => sr.id === String(transferRequestRow.id ?? ''));
  }, [transferRequestRow, tracker.serviceRequests]);

  const clientForDrawer = useMemo(() => {
    if (!focusedRow) return null;
    if (tracker.isMock) {
      const r = focusedRow as Partial<EpisodeRow & VerificationErrorRow & PartialSubmission>;
      return {
        ...mockClientSummary,
        id: r.clientId ?? mockClientSummary.id,
        name: r.clientName ?? mockClientSummary.name,
        episodes: focusedEpisode
          ? [focusedEpisode, ...mockClientSummary.episodes.filter((e) => e.id !== focusedEpisode.id)]
          : mockClientSummary.episodes,
      };
    }
    return liveClientSummaryFromRow(focusedRow, focusedEpisode);
  }, [focusedRow, focusedEpisode, tracker.isMock]);

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <div>
            <Group gap="sm" align="baseline">
              <Title order={2}>Service Request Tracker</Title>
              {userRole !== 'Unify Admin' ? <LocalhostOnlyBadge /> : null}
              {tracker.loading && <Loader size="xs" />}
            </Group>
            <Text c="dimmed" size="sm">
              Manage episodes of care through the lifecycle — eligibility, screening, matching, and active care.
            </Text>
          </div>
          <Button
            leftSection={<IconPlus size={16} />}
            color="gold"
            onClick={handleOpenAddEpisode}
          >
            Add episode of care
          </Button>
        </Group>
        <div>
          {tracker.error && (
            <Text size="xs" c="red.7" mt="xs">
              {tracker.error}
            </Text>
          )}
        </div>

        <Tabs
          value={activeTab ?? undefined}
          onChange={(v) => setActiveTab(v as LifecycleStage)}
          styles={{
            tab: {
              transition: 'all 0.2s ease',
            },
            list: {
              borderBottom: 'none',
            },
          }}
          style={{
            '--tabs-list-border-size': '2px',
          } as React.CSSProperties}
        >
          <Tabs.List style={{ flexWrap: 'wrap' }}>
            {TABS_ORDER.map((stage) => (
              <Tabs.Tab key={stage} value={stage}>
                <Group gap={6} wrap="nowrap">
                  {STAGE_LABELS[stage]}
                  <Badge size="sm" variant="light" circle>
                    {stageCounts[stage]}
                  </Badge>
                </Group>
              </Tabs.Tab>
            ))}
          </Tabs.List>

          {TABS_ORDER.map((stage) => (
            <Tabs.Panel key={stage} value={stage} mt="md">
              <Paper withBorder radius="md" p="lg">
                {stage === 'needs-verification' ? (
                  <EligibilityTable
                    rows={eligibilityRows}
                    onAction={setEligibilityActionRow}
                    presenceByRow={presenceByRow}
                  />
                ) : (
                  <StageTab
                    stage={stage}
                    rows={rowsByStage[stage]}
                    practiceOptions={practiceOptions}
                    presenceByRow={presenceByRow}
                    onRowClick={handleRowClick}
                    onOpenMatchingPanel={handleOpenMatching}
                    onRowAction={handleRowAction}
                    onContextualAction={handleContextualAction}
                    onViewScreeningResults={setScreeningResultsRow}
                    onScheduleCall={setScheduleCallRow}
                    onMarkUninterested={setUninterestedRow}
                    onManualAssign={(row) => setManualAssignRow(row as unknown as EpisodeRow)}
                    onViewTransferRequest={(row) => setTransferRequestRow(row as unknown as EpisodeRow)}
                    onInitiateTransfer={(row) => setInitiateTransferRow(row as unknown as EpisodeRow)}
                  />
                )}
              </Paper>
            </Tabs.Panel>
          ))}
        </Tabs>
      </Stack>

      <ClientDetailDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        client={clientForDrawer}
        focusedEpisode={focusedEpisode}
        coverages={
          tracker.isMock ? mockCoverages : liveCoveragesFromEpisodeRow(focusedEpisode)
        }
        useMockChartData={tracker.isMock}
        serviceRequest={drawerServiceRequest}
        onTransferComplete={() => {
          tracker.refresh();
          reloadCaseload();
          setDrawerOpen(false);
        }}
      />

      <MatchingModal
        open={matchingPanelOpen}
        onClose={() => setMatchingPanelOpen(false)}
        serviceRequest={matchingServiceRequest}
        clientId={matchingRow?.clientId}
        clientName={matchingRow?.clientName ?? ''}
        episodeServiceType={matchingRow?.episodeServiceType ?? ''}
        state={matchingRow?.state}
        payer={matchingRow?.payer}
      />

      <ManualAssignModal
        open={manualAssignRow !== null}
        onClose={() => setManualAssignRow(null)}
        clientName={manualAssignRow?.clientName ?? ''}
        clientId={manualAssignRow?.clientId}
      />

      <VerificationActionDrawer
        open={verificationAction !== null}
        onClose={() => setVerificationAction(null)}
        action={verificationAction?.action ?? null}
        row={verificationAction ? (verificationAction.row as unknown as VerificationErrorRow) : null}
        onConfirm={(_action, _payload) => {
          // Mock: could create CoverageEligibilityResponse / update Coverage in Medplum here
          setVerificationAction(null);
        }}
      />

      <EpisodeActionDrawer
        open={episodeAction !== null}
        onClose={() => setEpisodeAction(null)}
        action={episodeAction?.action ?? null}
        row={episodeAction?.row ?? null}
      />

      <EligibilityActionModal
        open={eligibilityActionRow !== null}
        onClose={() => setEligibilityActionRow(null)}
        row={eligibilityActionRow}
        onSuccess={() => {
          setEligibilityActionRow(null);
          tracker.refresh();
        }}
      />

      <ScreeningFormModal
        open={screeningActionRow !== null}
        onClose={() => setScreeningActionRow(null)}
        row={screeningActionRow}
        onSaved={() => {
          setScreeningActionRow(null);
          tracker.refresh();
        }}
      />
      <ScreeningResultsModal
        open={screeningResultsRow !== null}
        onClose={() => setScreeningResultsRow(null)}
        patientId={
          screeningResultsRow
            ? String((screeningResultsRow as Partial<EpisodeRow>).clientId ?? '')
            : null
        }
        clientName={(screeningResultsRow as Partial<EpisodeRow> | null)?.clientName}
      />
      <ScheduleCallModal
        open={scheduleCallRow !== null}
        onClose={() => setScheduleCallRow(null)}
        patientId={
          scheduleCallRow
            ? String((scheduleCallRow as Partial<EpisodeRow>).clientId ?? '')
            : null
        }
        clientName={(scheduleCallRow as Partial<EpisodeRow> | null)?.clientName}
        onScheduled={() => tracker.refresh()}
      />
      <Modal
        opened={uninterestedRow !== null}
        onClose={() => setUninterestedRow(null)}
        title="Mark as uninterested"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            Are you sure you want to mark{' '}
            <strong>{(uninterestedRow as Partial<EpisodeRow> | null)?.clientName ?? 'this client'}</strong>{' '}
            as no longer interested? This should only be done after confirming with the client.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setUninterestedRow(null)}>
              Cancel
            </Button>
            <Button color="red" onClick={() => setUninterestedRow(null)}>
              Mark as uninterested
            </Button>
          </Group>
        </Stack>
      </Modal>
      <ConsultationNoteModal
        open={consultationActionRow !== null}
        onClose={() => setConsultationActionRow(null)}
        row={consultationActionRow}
        onSave={() => setConsultationActionRow(null)}
      />

      <TransferRequestModal
        opened={transferRequestRow !== null}
        onClose={() => setTransferRequestRow(null)}
        episode={transferRequestRow}
        serviceRequest={transferRequestServiceRequest}
      />

      <TransferModal
        opened={initiateTransferRow !== null}
        onClose={() => setInitiateTransferRow(null)}
        context={initiateTransferRow ? transferContextFromRow(initiateTransferRow) : null}
        onSuccess={() => {
          tracker.refresh();
          reloadCaseload();
          setInitiateTransferRow(null);
        }}
      />

      <TrackerAddEpisodeModal
        opened={addEpisodeOpen}
        onClose={handleCloseAddEpisode}
        fixedPatientId={addEpisodePatientId}
        onCreated={() => {
          tracker.refresh();
          reloadCaseload();
        }}
      />
    </Container>
  );
}
